/**
 * Tab "MCP del Brain" de /ia-native/observability (rehecho 09/10/2026).
 *
 * Hasta hoy mostraba `v_mcp_unified`: el logging MCP de Gustavo (media-mcp,
 * analytics-mcp…), que no es de la plataforma. Ahora muestra NUESTRO MCP del
 * Brain (`ask_brain` desde claude.ai, Claude Desktop/Code, Cursor…): consultas
 * por día, por app, personas que lo usan y costo. Los datos salen de
 * `getBrainChannels` (lib/internal-brain-adoption.ts), el mismo corte por canal
 * del tab de adopción de Internal, sin cuentas de servicio. No se listan las
 * preguntas: la audiencia de /ia-native es más amplia que la de Internal.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Users, DollarSign, AppWindow, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatCard, LoaderBlock, fmtUSDCents, fmtNum } from './shared';
import { useObsBrainMcp } from '@/hooks/useObservability';
import { BEDROCK_WINDOWS, type BedrockWindowDays } from '../../../../shared/bedrock-features';
import { addDaysISO, todayUTC } from '../../../../shared/dates';

const APP_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#0ea5e9', '#a855f7', '#94a3b8'];
const shortDay = (d: string) => d.slice(5);
// reuse-ok: mismas tablas que el resto de components/observability (header con fondo); InternalTable es el estilo compacto de components/internal
const thClass = 'px-4 py-2 text-left';
const thRight = 'px-4 py-2 text-right';

export function ObsMCPTab() {
  const { t } = useTranslation();
  const [days, setDays] = useState<BedrockWindowDays>(30);
  const q = useObsBrainMcp(days);
  const tr = (k: string, o?: Record<string, unknown>) => t(`observability.mcp.${k}`, o);
  const appLabel = (app: string) => t(`observability.mcp.app_${app}`, { defaultValue: app });

  const agg = useMemo(() => {
    const d = q.data;
    const apps = (d?.mcpByApp ?? []).map((r) => ({ app: r.clientApp, queries: r.queries, users: r.users, costUsd: r.costUsd, p50Ms: r.p50Ms }));
    // Serie sin huecos: un día sin consultas se ve como 0, no desaparece del gráfico.
    const byDay = new Map((d?.mcpPerDay ?? []).map((r) => [r.day, r]));
    const perDay: Array<{ day: string; queries: number; users: number }> = [];
    for (let i = days - 1; i >= 0; i--) {
      const day = addDaysISO(todayUTC(), -i); // la serie es por día UTC (FORMAT_TIMESTAMP en BigQuery)
      const r = byDay.get(day);
      perDay.push({ day, queries: r?.queries ?? 0, users: r?.users ?? 0 });
    }
    return { apps, perDay, users: d?.mcpTopUsers ?? [], appsCount: apps.filter((a) => a.app !== 'sin_dato').length };
  }, [q.data, days]);

  const totals = q.data?.mcp ?? { queries: 0, users: 0, costUsd: 0 };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-xs text-muted-foreground">{tr('intro')}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('observability.bedrock.period_label')}</span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v) as BedrockWindowDays)}>
            <SelectTrigger className="h-8 w-40 text-sm" aria-label={t('observability.bedrock.period_label')}><SelectValue /></SelectTrigger>
            <SelectContent>
              {BEDROCK_WINDOWS.map((w) => <SelectItem key={w} value={String(w)}>{t(`observability.bedrock.period_${w}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {q.isError && (
        <Card className="flex items-start gap-3 p-4 text-amber-600">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p className="text-sm">{tr('load_error', { error: q.error?.message ?? '' })}</p>
        </Card>
      )}
      {q.data?.error && (
        <Card className="flex items-start gap-3 p-4 text-amber-600">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p className="text-sm">{tr('load_error', { error: q.data.error })}</p>
        </Card>
      )}
      {q.isLoading && <LoaderBlock />}

      {!q.isLoading && !q.isError && (<>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard icon={Bot} tone="purple" label={tr('stat_queries')} value={fmtNum(totals.queries)} />
        <StatCard icon={Users} tone="green" label={tr('stat_users')} value={fmtNum(totals.users)} />
        <StatCard icon={AppWindow} tone="blue" label={tr('stat_apps')} value={String(agg.appsCount)} sub={agg.apps.slice(0, 3).map((a) => appLabel(a.app)).join(' · ') || undefined} />
        <StatCard icon={DollarSign} tone="amber" label={tr('stat_cost')} value={fmtUSDCents(totals.costUsd)} sub={tr('stat_cost_sub')} />
      </div>

      <Card className="p-4">
        <h3 className="mb-1 text-sm font-semibold">{tr('per_day_title')}</h3>
        <p className="mb-3 text-xs text-muted-foreground">{tr('per_day_subtitle')}</p>
        {totals.queries === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{tr('no_data')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={agg.perDay} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip formatter={(v, name) => [fmtNum(Number(v)), String(name)]} labelFormatter={(d) => String(d)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="queries" name={tr('col_queries')} fill="#6366f1" isAnimationActive={false} />
              <Bar dataKey="users" name={tr('col_users')} fill="#10b981" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold">{tr('by_app_title')}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{tr('by_app_subtitle')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                  <th className={thClass}>{tr('col_app')}</th>
                  <th className={thRight}>{tr('col_queries')}</th>
                  <th className={thRight}>{tr('col_users')}</th>
                  <th className={thRight}>{tr('col_p50')}</th>
                  <th className={thRight}>{tr('col_cost')}</th>
                </tr>
              </thead>
              <tbody>
                {agg.apps.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">{tr('no_data')}</td></tr>
                ) : agg.apps.map((a, i) => (
                  <tr key={a.app} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2.5"><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: APP_COLORS[i % APP_COLORS.length] }} />{appLabel(a.app)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(a.queries)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(a.users)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{a.p50Ms != null ? `${fmtNum(a.p50Ms)} ms` : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">{fmtUSDCents(a.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold">{tr('who_title')}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{tr('who_subtitle')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                  <th className={thClass}>{t('observability.bedrock.col_user')}</th>
                  <th className={thClass}>{tr('col_apps')}</th>
                  <th className={thRight}>{tr('col_queries')}</th>
                  <th className={thRight}>{tr('col_last')}</th>
                </tr>
              </thead>
              <tbody>
                {agg.users.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">{tr('no_data')}</td></tr>
                ) : agg.users.map((u) => (
                  <tr key={u.userEmail} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground" title={u.userEmail}>{u.userEmail}</td>
                    <td className="px-4 py-2.5 text-xs">{u.apps.map(appLabel).join(', ')}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(u.queries)}</td>
                    <td className="px-4 py-2.5 text-right text-xs tabular-nums text-muted-foreground">{u.lastQuery ? u.lastQuery.slice(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      </>)}
    </div>
  );
}
